<template>
  <div class="cfdi-filters-clean" v-if="shouldShowFilterButton">
    <button
      class="btn btn-sm filter-trigger-btn"
      :class="hasAnyActiveFilters ? 'btn-primary' : 'btn-light'"
      type="button"
      @click="showAdvancedModal = true"
      :title="hasAnyActiveFilters ? `Filtros activos (${activeFiltersCount})` : 'Filtros'"
    >
      <i class="fa fa-filter"></i>
      <span class="filter-trigger-text">Filtrar</span>
      <span class="filter-trigger-count" v-if="hasAnyActiveFilters">{{ activeFiltersCount }}</span>
    </button>

    <button
      v-if="showExport"
      class="btn btn-sm btn-success export-trigger-btn"
      type="button"
      @click="$emit('export', clone(local))"
      title="Exportar listado a Excel"
    >
      <i class="fa fa-file-excel-o"></i>
      <span class="filter-trigger-text">Exportar</span>
    </button>

    <button
      v-if="showDownload"
      class="btn btn-sm export-trigger-btn download-trigger-btn"
      type="button"
      :disabled="downloadLoading"
      @click="$emit('download')"
      title="Solicitar descarga masiva XML/PDF"
    >
      <i class="fa fa-download"></i>
      <span class="filter-trigger-text">{{ downloadLoading ? 'Solicitando...' : 'Descargar XMLs y PDFs' }}</span>
    </button>

    <CfdiAdvancedFilterModal
      v-if="showAdvancedModal"
      :filters="local.advanced"
      :search="local.search"
      :status="local.status"
      :method="local.method"
      :period="local.period"
      :showSearch="showSearch"
      :showStatus="showStatus"
      :showMethod="showMethod"
      :showPeriod="showPeriod"
      :showAdvanced="showAdvanced"
      :showPersonalDeductions="showPersonalDeductions"
      :personalDeductions="!!local.personal_deductions"
      :nature="nature"
      :group="group"
      @close="showAdvancedModal = false"
      @apply="onApplyAllFilters"
    />
  </div>
</template>

<script>
import CfdiAdvancedFilterModal from "@/components/cfdis/CfdiAdvancedFilterModal.vue";
import { GLOBAL_PERIOD_EVENT, getGlobalPeriod } from "@/services/globalPeriod";

export default {
  name: "CfdiFiltersBar",
  components: { CfdiAdvancedFilterModal },

  props: {
    modelValue: {
      type: Object,
      default: () => ({
        nature: "",
        group: "",
        period: "",
        search: "",
        status: "ALL",
        method: "ALL",
        personal_deductions: false,
        advanced: [],
      }),
    },
    nature: { type: String, default: "" },
    group: { type: String, default: "" },

    autoApply: { type: Boolean, default: true },

    showSearch: { type: Boolean, default: true },
    showPeriod: { type: Boolean, default: true },
    showAdvanced: { type: Boolean, default: true },
    showStatus: { type: Boolean, default: true },
    showMethod: { type: Boolean, default: true },
    showExport: { type: Boolean, default: false },
    showDownload: { type: Boolean, default: false },
    downloadLoading: { type: Boolean, default: false },
    showPersonalDeductions: { type: Boolean, default: false },
  },

  emits: ["update:modelValue", "apply", "export", "download"],

  data() {
    return {
      local: this.clone(this.modelValue),
      showAdvancedModal: false,
      available_filters: [],
      _syncingFromParent: false,
    };
  },

  computed: {
    hasAdvanced() {
      return Array.isArray(this.local.advanced) && this.local.advanced.length > 0;
    },
    shouldShowFilterButton() {
      return this.showAdvanced || this.showSearch || this.showStatus || this.showMethod || this.showPeriod;
    },
    hasAnyActiveFilters() {
      return this.activeFiltersCount > 0;
    },
    activeFiltersCount() {
      let count = 0;

      if (this.showSearch && String(this.local.search || '').trim() !== '') count += 1;
      if (this.showStatus && String(this.local.status || 'ALL') !== 'ALL') count += 1;
      if (this.showMethod && String(this.local.method || 'ALL') !== 'ALL') count += 1;
      if (this.showPersonalDeductions && !!this.local.personal_deductions) count += 1;
      if (this.showAdvanced && this.hasAdvanced) count += this.local.advanced.length;

      return count;
    },
  },

  watch: {
    modelValue: {
      deep: true,
      handler(val) {
        this._syncingFromParent = true;
        this.local = this.clone(val);
        this.$nextTick(() => (this._syncingFromParent = false));
      },
    },
  },

  async mounted() {
    if (!this.local.period) {
      this.local.period = getGlobalPeriod();
      this.commit(false);
    }

    window.addEventListener(GLOBAL_PERIOD_EVENT, this.handleGlobalPeriodChanged);
  },

  beforeDestroy() {
    window.removeEventListener(GLOBAL_PERIOD_EVENT, this.handleGlobalPeriodChanged);
  },

  methods: {
    clone(obj) {
      return JSON.parse(JSON.stringify(obj || {}));
    },

    commit(shouldApply = this.autoApply) {
      // Si estamos sincronizando desde el padre, no emitimos de regreso
      if (this._syncingFromParent) return;

      const payload = this.clone(this.local);
      this.$emit("update:modelValue", payload);
      if (shouldApply) this.$emit("apply", payload);
    },

    handleGlobalPeriodChanged(event) {
      const period = String(event?.detail?.period || '').trim();

      if (!period || period === this.local.period) {
        return;
      }

      this.local.period = period;
      this.commit(true);
    },

    onApplyAllFilters(payload) {
      const data = payload && typeof payload === 'object' ? payload : {};

      if (this.showSearch) this.local.search = data.search ?? this.local.search;
      if (this.showStatus) this.local.status = data.status ?? this.local.status;
      if (this.showMethod) this.local.method = data.method ?? this.local.method;
      if (this.showPeriod) this.local.period = data.period ?? this.local.period;
      if (this.showPersonalDeductions) this.local.personal_deductions = !!(data.personal_deductions ?? this.local.personal_deductions);
      if (this.showAdvanced) this.local.advanced = Array.isArray(data.advanced) ? data.advanced : this.local.advanced;

      this.showAdvancedModal = false;
      this.commit(true);
    },
  },
};
</script>

<style scoped>
.cfdi-filters-clean {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.filter-trigger-btn {
  border: 1px solid var(--sidebar-border);
  height: 32px;
  min-height: 32px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 0 10px;
}

.filter-trigger-text {
  font-size: 11px;
  font-weight: 600;
}

.filter-trigger-count {
  min-width: 16px;
  height: 16px;
  padding: 0 4px;
  border-radius: 10px;
  background: var(--white);
  color: var(--theme-deafult);
  font-size: 10px;
  font-weight: 700;
  line-height: 16px;
}

.export-trigger-btn {
  height: 32px;
  min-height: 32px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 0 10px;
}

.download-trigger-btn {
  color: var(--white);
  background: linear-gradient(135deg, #b55bff 0%, #9f4df2 100%);
  border: 1px solid #9f4df2;
}

.download-trigger-btn:hover,
.download-trigger-btn:focus,
.download-trigger-btn:active {
  color: var(--white);
  background: linear-gradient(135deg, #a949f5 0%, #943ee6 100%);
  border-color: #943ee6;
}

.download-trigger-btn:disabled {
  color: rgba(255, 255, 255, 0.9);
  background: linear-gradient(135deg, #d7a7ff 0%, #c994fa 100%);
  border-color: #c994fa;
}
</style>
